import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { ENGINE_LABELS } from "@/config";
import { Page, PageHeader, Card, Segmented, Chip, Tag, Label11, Loading, int, plural, ago } from "@/components/common";
import { KIND_LABELS, kindOf, isDocuments } from "@/lib/workKinds";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import ChangeReport, { TRIGGER, InvestigateButton } from "@/components/ChangeReport";
import { GitPullRequest, ExternalLink } from "lucide-react";

// design_handoff_crewbench/screens/Change Reports.dc.html: every session's latest change report, filtered, with the
// selected one in full. ?report= opens a given one (the Overview's Review changes). Opening a report marks it seen
// for this founder: it leaves their Overview list and the sidebar's count, and still waits for the other founders.
const FILTERS = [["all", "All", () => true], ["submitted", "Submitted", (r) => r.trigger === "submit"], ["flagged", "With flags", (r) => r.flags.length > 0]];
const flagged = (r) => r.flags.reduce((a, f) => a + f.paths.length, 0);

export default function Reports() {
  const qc = useQueryClient();
  const [params] = useSearchParams();
  const { data, isLoading } = useQuery({ queryKey: ["reports"], queryFn: async () => (await api.get("/reports")).data, refetchInterval: 10000 });
  const { data: team } = useQuery({ queryKey: ["team"], queryFn: async () => (await api.get("/team")).data });
  const { data: objs } = useQuery({ queryKey: ["objectives"], queryFn: async () => (await api.get("/objectives")).data });
  const [filter, setFilter] = useState("all");
  const { data: counts } = useQuery({ queryKey: ["overview-counts"], queryFn: async () => (await api.get("/overview/counts")).data });
  const [selected, setSelected] = useState(params.get("report"));
  const seen = useMutation({
    mutationFn: async (id) => api.post(`/reports/${id}/seen`),
    onSuccess: () => ["overview", "overview-counts"].forEach((k) => qc.invalidateQueries({ queryKey: [k] })),
  });
  const open = (r) => { setSelected(r.id); if (r.trigger === "submit") seen.mutate(r.id); };
  const linked = useRef(params.get("report"));
  useEffect(() => { if (linked.current) { seen.mutate(linked.current); linked.current = null; } }, []);  // eslint-disable-line react-hooks/exhaustive-deps

  // each session's latest report (the list comes newest first)
  const latest = [];
  const sessions = new Set();
  for (const r of data?.reports || []) if (!sessions.has(r.session_id)) { sessions.add(r.session_id); latest.push(r); }
  const pass = FILTERS.find(([k]) => k === filter)[2];
  const shown = latest.filter(pass);
  const current = shown.find((r) => r.id === selected) || shown[0];

  const people = Object.fromEntries((team?.members || []).map((m) => [m.id, m.name || m.email]));
  const titles = Object.fromEntries((objs?.objectives || []).map((o) => [o.id, o.title]));
  // submitted, its pull request (if any) not merged or closed, and not opened by this founder yet
  const waiting = counts?.reports || 0;
  const switcher = (
    <Segmented label="Filter" value={filter} onChange={(v) => { setFilter(v); setSelected(null); }}
      options={FILTERS.map(([k, label, p]) => [k, `${label} ${latest.filter(p).length}`])} testid="report-filter" />
  );
  return (
    <Page data-testid="owner-reports">
      <PageHeader eyebrow="Change reports · Built from git" title="Everything each session changed in your code and documents."
        status={waiting === 0 ? "Nothing is waiting for your review." : `${waiting} submitted for your review.`} actions={switcher} />
      {isLoading ? <Loading /> : !latest.length ? (
        <Card className="p-5 text-muted-foreground">No change reports yet. One is created when someone on your team submits or ends a session.</Card>
      ) : (
        <div className="flex flex-wrap gap-5 items-start">
          <div className="flex-[1_1_260px] max-w-[340px] flex flex-col gap-px bg-gr-hairline rounded-[10px] overflow-hidden">
            {shown.map((r) => (
              <button key={r.id} type="button" onClick={() => open(r)} data-testid={`report-row-${r.id}`}
                className="relative text-left bg-card flex flex-col gap-1 px-4 py-3 transition-colors hover:bg-gr-hover focus-visible:outline focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring">
                {current?.id === r.id && <span className="absolute inset-0 bg-gr-hover-strong pointer-events-none" aria-hidden="true" />}
                <span className="flex justify-between items-baseline gap-3">
                  <span className="font-semibold">{titles[r.objective_id] || "No objective"}</span>
                  <span className="text-sm text-muted-foreground whitespace-nowrap">{ago(r.created_at)}</span>
                </span>
                <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                  <span className="text-sm text-muted-foreground">Session {r.short_id} · {people[r.coder_id] || "A former member"}</span>
                  <Tag tone="muted" className="border-gr-hairline">{KIND_LABELS[kindOf(r)]}</Tag>
                </span>
                <span className="flex flex-wrap items-center gap-x-2.5 gap-y-1 text-sm tabular-nums">
                  {isDocuments(kindOf(r))
                    ? <span className="text-muted-foreground">{plural(r.totals.files, "document")}</span>
                    : <span className="text-muted-foreground">{plural(r.totals.files, "file")} <span className="text-gr-success">+{int(r.totals.added_lines)}</span> <span className="text-gr-danger">−{int(r.totals.removed_lines)}</span></span>}
                  {r.trigger === "submit" && <Chip className="py-0">Submitted</Chip>}
                  {flagged(r) > 0 && <span className="text-gr-warning">{flagged(r)} to check</span>}
                </span>
              </button>
            ))}
            {shown.length === 0 && <div className="bg-card px-4 py-5 text-muted-foreground">No reports match this filter.</div>}
          </div>
          {current && (
            <Card className={cn("flex-[100_1_340px] min-w-0 px-6 py-5 flex flex-col gap-5")}>
              <div className="flex flex-wrap justify-between items-start gap-4">
                <div className="flex flex-col gap-1 min-w-0">
                  <Label11>Session {current.short_id} · {TRIGGER[current.trigger] || current.trigger}</Label11>
                  <h2 className="m-0 font-display text-[22px] leading-[1.25] tracking-[0.02em] font-semibold">{titles[current.objective_id] || "No objective"}</h2>
                  <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                    <span className="text-sm text-muted-foreground">{people[current.coder_id] || "A former member"} · {ENGINE_LABELS[current.engine] || current.engine}</span>
                    <Tag tone="muted" className="border-gr-hairline" data-testid="report-kind">{KIND_LABELS[kindOf(current)]}</Tag>
                  </span>
                </div>
                <div className="flex flex-wrap gap-2">
                  <InvestigateButton sessionId={current.session_id} />
                  {current.pull_request?.number && (
                    <Button variant="outline" asChild>
                      {isDocuments(kindOf(current))
                        ? <a href={current.pull_request.url} target="_blank" rel="noreferrer" data-testid="pull-request"><ExternalLink />Open the review on GitHub</a>
                        : <a href={current.pull_request.url} target="_blank" rel="noreferrer" data-testid="pull-request"><GitPullRequest />Pull request #{current.pull_request.number}</a>}
                    </Button>
                  )}
                </div>
              </div>
              <ChangeReport report={current} pullRequest={current.pull_request} plainMeta footnote investigate={false} />
            </Card>
          )}
        </div>
      )}
    </Page>
  );
}
